import { getPersistedExperimentTreatment } from '@ms/yammer-data/dist/domains/experiments/selectors';
import { Experiment } from '@ms/yammer-data/dist/state/types';
import {
  endAndReportPerformanceMeasure,
  reportError,
  reportInfo,
  startPerformanceMeasure,
} from '@ms/yammer-data/dist/telemetry';
import { ErrorCode, InfoEventName, PerformanceEventName } from '@ms/yammer-telemetry';
import { isAuthRedirect } from '@ms/yammer-web-authenticators';
import {
  isUnpartitionedThirdPartyCookieSupported,
  reportThirdPartyCookieEvent,
} from '@ms/yammer-web-support/dist/cookies';
import { isAuthCookieAvailable, writeHostingCookies } from '@ms/yammer-web-support/dist/hosting';
import { authReload, getUrlRootPath } from '@ms/yammer-web-support/dist/location';

import { setupAuthenticator } from '../../auth';
import config from '../../config';
import { persistConfig } from '../../persistedState';
import { setLocaleFromUserData } from '../1-main/bootstrapActions';
import { createYammerEmbedStore } from '../1-main/createStore';
import { isPermanentFailure } from '../1-main/isPermanentFailure';
import { loadLoginRenderer } from '../3-render';

import { configure as configureTelemetry } from './telemetry';
import { renderThirdPartyCookieDisabled } from './thirdPartyCookie';

export const bootstrap = async () => {
  const isPartitionedCookieExperimentEnabledPromise = getPersistedExperimentTreatment(
    persistConfig.key,
    Experiment.ThirdPartyCookieDeprecationMitigation
  );
  const isStorageAccessApiEnabledPromise = getPersistedExperimentTreatment(
    persistConfig.key,
    Experiment.StorageAccessAPI
  );

  const authenticator = await setupAuthenticator();

  await authenticator.initializeCurrentAccount();

  await configureTelemetry();

  if (isAuthRedirect()) {
    return;
  }
  let endedWithError = false;

  reportThirdPartyCookieEvent();

  const isPartitionedCookieExperimentEnabled = !!(await isPartitionedCookieExperimentEnabledPromise);

  const measure = startPerformanceMeasure(PerformanceEventName.AuthBootstrap);
  try {
    const yammerToken = await authenticator.acquireToken([config.msal.resources.yammer]);
    const partitioned = isPartitionedCookieExperimentEnabled && !isUnpartitionedThirdPartyCookieSupported();
    writeHostingCookies({
      yammerToken,
      shouldSetSameSite: true,
      partitioned,
    });
  } catch (err) {
    endedWithError = isPermanentFailure(err);
    reportError({
      error: err,
      eventProperties: { errorCode: ErrorCode.AuthBootstrapFailed },
    });

    const { store } = createYammerEmbedStore();
    const { dispatch, getState } = store;
    setLocaleFromUserData(dispatch, getState());
    const render = await loadLoginRenderer();
    const renderRoot = document.getElementById('root') as HTMLElement;
    render({ store, target: renderRoot, authenticator });

    throw err;
  } finally {
    reportInfo({
      eventName: InfoEventName.AuthBootstrapInfo,
      eventProperties: {
        isExecutingInIFrame: window.top !== window.self,
        urlRootPath: getUrlRootPath(window.location.pathname),
      },
    });
    endAndReportPerformanceMeasure({
      ...measure,
      eventProperties: { endedWithError },
    });
  }

  const isStorageAccessApiFeatureEnabled = !!(await isStorageAccessApiEnabledPromise);
  const authCookieAvailable = isAuthCookieAvailable();

  if (!authCookieAvailable && !isStorageAccessApiFeatureEnabled) {
    await renderThirdPartyCookieDisabled();

    return;
  }

  authReload(isStorageAccessApiFeatureEnabled && !authCookieAvailable);
};
